import type { DateRange, DateRangeKey } from '../types';

const formatLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number): Date => {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
};

const addMonths = (date: Date, months: number): Date => {
  const next = startOfDay(date);
  next.setMonth(next.getMonth() + months);
  return next;
};

const startOfIsoWeek = (date: Date): Date => {
  const d = startOfDay(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  return addDays(d, diffToMonday);
};

export const computeRange = (key: DateRangeKey, anchor: Date): DateRange => {
  const today = startOfDay(anchor);

  if (key === 'today' || key === 'day') {
    const iso = formatLocal(today);
    return { key, dateFrom: iso, dateTo: iso, isSingleDay: true };
  }

  if (key === 'yesterday') {
    const iso = formatLocal(addDays(today, -1));
    return { key, dateFrom: iso, dateTo: iso, isSingleDay: true };
  }

  if (key === 'last_week') {
    return {
      key,
      dateFrom: formatLocal(addDays(today, -6)),
      dateTo: formatLocal(today),
      isSingleDay: false,
    };
  }

  if (key === 'last_month') {
    return {
      key,
      dateFrom: formatLocal(addDays(addMonths(today, -1), 1)),
      dateTo: formatLocal(today),
      isSingleDay: false,
    };
  }

  if (key === 'last_3_months') {
    return {
      key,
      dateFrom: formatLocal(addDays(addMonths(today, -3), 1)),
      dateTo: formatLocal(today),
      isSingleDay: false,
    };
  }

  if (key === 'previous_week') {
    const thisMonday = startOfIsoWeek(today);
    const prevMonday = addDays(thisMonday, -7);
    const prevSunday = addDays(thisMonday, -1);
    return {
      key,
      dateFrom: formatLocal(prevMonday),
      dateTo: formatLocal(prevSunday),
      isSingleDay: false,
    };
  }

  // previous_month
  const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfPrevMonth = addDays(firstOfThisMonth, -1);
  const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
  return {
    key,
    dateFrom: formatLocal(firstOfPrevMonth),
    dateTo: formatLocal(lastOfPrevMonth),
    isSingleDay: false,
  };
};

export const eachDayInRange = (range: DateRange): string[] => {
  const result: string[] = [];
  const [fy, fm, fd] = range.dateFrom.split('-').map(Number);
  const [ty, tm, td] = range.dateTo.split('-').map(Number);
  const start = new Date(fy, fm - 1, fd);
  const end = new Date(ty, tm - 1, td);
  for (let d = start; d <= end; d = addDays(d, 1)) {
    result.push(formatLocal(d));
  }
  return result;
};
